import { Workstation } from 'app/containers/OrderDetails/types';
import { ProductionPiece } from 'app/models/ProductionPiece';
import {
  ComputePickingSubPagesOptions,
  PickingSubPage,
  PickingSubPageCollection,
} from './types';
import { createComparePickingPresentationPieces } from './comparePickingPresentationPieces';
import { isDrawerHardwareReference } from './drawerPieceClassification';
import { orderByLocation } from '../pickingOrder';

const DEFAULT_LINES_PER_PAGE = 26;

const isDrawerHardwarePiece = (
  piece: ProductionPiece,
  workstation?: Workstation,
): boolean => {
  const shouldRouteToHardwareSection =
    workstation === Workstation.Drawer || piece.station === Workstation.Drawer;

  return (
    shouldRouteToHardwareSection &&
    !piece.cutting &&
    isDrawerHardwareReference(piece.reference)
  );
};

const sortPieces = (pieces: ProductionPiece[]): ProductionPiece[] => {
  return [...pieces].sort(createComparePickingPresentationPieces());
};

const cloneWithWidthFlag = (
  piece: ProductionPiece,
  isWidth62: boolean,
): ProductionPiece => ({
  ...piece,
  isWidth62,
});

const createEmptyPage = (): PickingSubPage => ({
  cabinetPieces: [],
  hardwarePieces: [],
  cuttingPieces: [],
  sparePartPieces: [],
  sparePartHardwarePieces: [],
  sparePartCuttingPieces: [],
});

const getSection = (page: PickingSubPage, index: number): ProductionPiece[] => {
  switch (index) {
    case 0:
      return page.cabinetPieces;
    case 1:
      return page.hardwarePieces;
    case 2:
      return page.cuttingPieces;
    case 3:
      return page.sparePartPieces;
    case 4:
      return page.sparePartHardwarePieces;
    case 5:
      return page.sparePartCuttingPieces;
    default:
      return page.cabinetPieces;
  }
};

export const computePickingSubPages = (
  pieces: ProductionPiece[],
  drawerPieces: ProductionPiece[] | undefined,
  options: ComputePickingSubPagesOptions,
): PickingSubPageCollection => {
  const {
    countTextLines,
    linesPerPage = DEFAULT_LINES_PER_PAGE,
    workstation,
    useLocationOrder = false,
  } = options;

  const hasWidth611 = pieces.some(
    piece => piece.type === 'cross piece' && piece.width === 611,
  );
  const hasWiderCrossPiece = pieces.some(
    piece => piece.type === 'cross piece' && piece.width > 611,
  );
  const isWidth62 = hasWidth611 && !hasWiderCrossPiece;

  const basePieces = sortPieces(
    pieces.map(piece => cloneWithWidthFlag(piece, isWidth62)),
  );

  const drawerPiecesList = drawerPieces ? drawerPieces.map(piece => ({ ...piece })) : [];
  const allPieces = [...basePieces, ...drawerPiecesList];

  let cabinetPieces: ProductionPiece[] = [];
  let hardwarePieces: ProductionPiece[] = [];
  const cuttingPieces: ProductionPiece[] = [];
  let sparePartPieces: ProductionPiece[] = [];
  let sparePartHardwarePieces: ProductionPiece[] = [];
  const sparePartCuttingPieces: ProductionPiece[] = [];

  allPieces.forEach(piece => {
    if (piece.drawer) return;

    if (piece.isSparePartInsideCabinetPackage) {
      if (piece.cutting) sparePartCuttingPieces.push(piece);
      else if (isDrawerHardwarePiece(piece, workstation)) {
        sparePartHardwarePieces.push(piece);
      } else {
        sparePartPieces.push(piece);
      }
      return;
    }

    if (piece.cutting) {
      cuttingPieces.push(piece);
      return;
    }

    if (isDrawerHardwarePiece(piece, workstation)) {
      hardwarePieces.push(piece);
      return;
    }

    cabinetPieces.push(piece);
  });

  if (drawerPiecesList.length > 0) {
    cabinetPieces = [...sparePartPieces, ...cabinetPieces];
    sparePartPieces = [];
  }

  if (useLocationOrder) {
    cabinetPieces = orderByLocation(cabinetPieces);
    sparePartPieces = orderByLocation(sparePartPieces);
    // Hardware keeps its own section and legacy fallback, then follows the walk.
    const compareHardware = createComparePickingPresentationPieces({
      forceDrawerHardwareSorting: true,
    });
    hardwarePieces = orderByLocation([...hardwarePieces].sort(compareHardware));
    sparePartHardwarePieces = orderByLocation(
      [...sparePartHardwarePieces].sort(compareHardware),
    );
  }

  const pages: PickingSubPageCollection = [createEmptyPage()];
  let currentPageIndex = 0;
  let counter = 0;

  const sourceLists = [
    cabinetPieces,
    hardwarePieces,
    cuttingPieces,
    sparePartPieces,
    sparePartHardwarePieces,
    sparePartCuttingPieces,
  ];

  sourceLists.forEach((list, sectionIndex) => {
    const hasHeaderLine = sectionIndex > 0 && list.length > 0;

    if (hasHeaderLine) counter += 1;

    list.forEach(piece => {
      if (counter >= linesPerPage) {
        counter = 0;
        currentPageIndex += 1;
        pages.push(createEmptyPage());

        if (
          sectionIndex > 0 &&
          list.length > 0 &&
          getSection(pages[currentPageIndex - 1], sectionIndex).length > 0
        ) {
          counter += 1;
        }
      }

      const linesForPiece = Math.max(1, countTextLines(piece));
      counter += linesForPiece;
      getSection(pages[currentPageIndex], sectionIndex).push(piece);
    });
  });

  return pages;
};

export default computePickingSubPages;
